import React, { useCallback, useEffect, useRef, useState } from 'react'
import Layout from '../../../components/Layout/Layout'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { saveAs } from 'file-saver'
import { useAuth } from '../../../context/auth'
import { Modal, Button } from 'antd'
import QtyLog from './QtyLog'
import AdminPackingListTable from './PackingListTable'
import ItemQty from './ItemQty'
import {
  enqueueScan,
  peekChunk,
  pendingCount,
  pendingOps,
  removeOps,
  requestPersistentStorage,
} from '../../../utils/scanQueue'

// Import Master is hidden for now. The button, its file input and the whole
// upload path are left intact - flip this to true to bring it back.
const SHOW_IMPORT_MASTER = false

// The server accepts up to 200 per request; 100 keeps each chunk small enough
// to retry cheaply on a bad link and well inside express.json()'s 100kb limit.
const SYNC_CHUNK = 100

// A scanned code can be either the item code or the barcode, and the server
// matches them case-insensitively - so the offline lookup has to as well.
const codeMatchesRow = (code, row) => {
  const wanted = String(code || '').trim().toLowerCase()
  if (!wanted) return false
  return (
    String(row.itemCode || '').toLowerCase() === wanted ||
    String(row.barCode || '').toLowerCase() === wanted
  )
}

const AdminPackingListDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchData, setSearchData] = useState('')
  const [packingDataList, setPackingDataList] = useState([])
  const [editMode, setEditMode] = useState(null)
  const [editField, setEditField] = useState(null)
  const [optionEnter, setOptionEnter] = useState('Take')
  const [filterQuery, setFilterQuery] = useState('')
  const fileImport = useRef(null)
  const masterFileImport = useRef(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingImport, setIsLoadingImport] = useState(false)
  const [isLoadingMasterImport, setIsLoadingMasterImport] = useState(false)

  const [visibleLogModal, setVisibleLogModal] = useState(false)
  const [currentItemId, setCurrentItemId] = useState(null)
  const [currentItemDetails, setCurrentItemDetails] = useState({})
  const [lastUpdate] = useState(new Date())

  const [visibleQtyModal, setVisibleQtyModal] = useState(false)
  const [originalValues, setOriginalValues] = useState({})
  const barcodeInputRef = useRef(null)
  const [focusBarcodeInput, setFocusBarcodeInput] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalPackingLists, setTotalPackingLists] = useState(0)
  // The mismatch export only means anything once sale figures exist - before
  // that every item still out reads as a mismatch. The server derives this
  // from the data rather than from a flag, so it is still right after a
  // reload, on a second operator's screen, and for events imported long ago.
  const [hasSaleData, setHasSaleData] = useState(false)

  // Offline mode is opt-in for now so the existing online flow is untouched
  // until this has been proven on the floor. It flips on by itself the first
  // time a scan fails with no response, so a link that dies mid-shift cannot
  // silently swallow scans while nobody thinks to switch over.
  const [offlineMode, setOfflineMode] = useState(false)
  const [pending, setPending] = useState(0)
  // The whole packing list for this event, keyed by item code and by barcode.
  // The table only lazy-loads 30 rows at a time, so this is what lets an
  // offline scan be both validated and displayed regardless of which page the
  // item lives on. Null means "we don't know" - validation then stays off
  // rather than guessing.
  const [listSnapshot, setListSnapshot] = useState(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [storageIsPersistent, setStorageIsPersistent] = useState(true)
  const syncingRef = useRef(false)

  // The rendered list, readable synchronously. Two scans can land before
  // React re-renders, and the second has to see the row the first added -
  // both to bump it rather than duplicate it, and to report correctly
  // whether the code is on screen.
  const listRef = useRef([])
  listRef.current = packingDataList

  const [auth] = useAuth()
  const userId = auth?.user?._id

  const [quantities, setQuantities] = useState({
    totalItems: 0,
    totalTakeQty: 0,
    totalReturnQty: 0,
    totalSaleQty: 0,
  })

  // Which filter the current `page` belongs to. Changing the filter while
  // paged in has to reset to page 1 *before* anything is fetched: as two
  // separate effects this fired fetchData(page N) against the new filter and
  // appended it to the old results, then fetched page 1 and replaced them.
  // On a slow link the two could also land out of order.
  const pagedForFilter = useRef(filterQuery)

  useEffect(() => {
    const shouldFetch = filterQuery.length > 3 || filterQuery === ''
    if (!shouldFetch) return

    if (pagedForFilter.current !== filterQuery) {
      pagedForFilter.current = filterQuery
      if (page !== 1) {
        // Re-runs this effect with page 1, which is the fetch that counts.
        setPage(1)
        return
      }
    }
    fetchData(page)
  }, [page, filterQuery])

  useEffect(() => {
    if (focusBarcodeInput) {
      barcodeInputRef.current?.focus()
      setFocusBarcodeInput(false)
    } else if (optionEnter) {
      barcodeInputRef.current?.focus()
    }
  }, [focusBarcodeInput, optionEnter])

  useEffect(() => {
    if (visibleQtyModal) {
      const fetchQtyData = async () => {
        try {
          const response = await axios.get(
            `/api/v1/admin/packing-list/quantities/${id}`
          )
          setQuantities(response.data)
        } catch (error) {
          console.error('Error fetching quantities:', error)
        }
      }
      fetchQtyData()
    }
  }, [visibleQtyModal, id])

  // Queued scans have not reached the server, so anything fetched from it is
  // missing them. Layer them back on or a refresh would appear to throw away
  // work the operator has already done.
  const applyPendingToList = async (rows, injectMissing = false) => {
    let ops = []
    try {
      ops = await pendingOps(id)
    } catch (error) {
      console.error('Could not read the scan queue:', error)
    }
    if (ops.length === 0) return rows

    const withDeltas = rows.map((row) => {
      const mine = ops.filter((op) => codeMatchesRow(op.code, row))
      if (mine.length === 0) return row
      const takes = mine.filter((op) => op.action === 'Take').length
      return {
        ...row,
        takeQty: (Number(row.takeQty) || 0) + takes,
        returnQty: (Number(row.returnQty) || 0) + (mine.length - takes),
        pendingScans: mine.length,
      }
    })

    if (!injectMissing || !listSnapshot) return withDeltas

    // Scans against items that are not on this page would otherwise disappear
    // from view on a refresh, even though they are still queued. Pull those
    // rows out of the snapshot and show them at the top.
    const alreadyShown = new Set(withDeltas.map((row) => row._id))
    const extras = []

    ops.forEach((op) => {
      const base = listSnapshot.get(String(op.code || '').trim().toLowerCase())
      if (!base || alreadyShown.has(base._id)) return

      const mine = ops.filter((other) => codeMatchesRow(other.code, base))
      const takes = mine.filter((other) => other.action === 'Take').length
      extras.push({
        ...base,
        takeQty: (Number(base.takeQty) || 0) + takes,
        returnQty: (Number(base.returnQty) || 0) + (mine.length - takes),
        pendingScans: mine.length,
      })
      alreadyShown.add(base._id)
    })

    return [...extras, ...withDeltas]
  }

  const fetchData = async (newPage = 1, showToast = false) => {
    setIsLoading(true)
    try {
      const response = await axios.get(
        `/api/v1/admin/packing-list/details/${id}`,
        {
          params: {
            userId,
            page: newPage,
            limit: 30,
            search: filterQuery,
          },
        }
      )

      const rows = await applyPendingToList(response.data.data, newPage === 1)
      if (newPage === 1) {
        setPackingDataList(rows)
      } else {
        // Drop anything already on screen. A row injected from the snapshot
        // after an offline scan will arrive again when its real page loads,
        // and the sort key (updatedAt) shifts as people scan, so a lazily
        // loaded page can legitimately overlap the one before it.
        setPackingDataList((prev) => {
          const seen = new Set(prev.map((row) => row._id))
          return [...prev, ...rows.filter((row) => !seen.has(row._id))]
        })
      }
      setTotalPackingLists(response.data.total)
      setHasSaleData(Boolean(response.data.hasSaleData))
      setHasMore(response.data.page < response.data.totalPages)
      if (showToast) {
        toast.success('Data is up to date')
      }
    } catch (error) {
      console.error('Failed to fetch packing list data:', error)
      setPackingDataList([])
    } finally {
      setIsLoading(false)
    }
  }

  // fetchData is redefined every render and closes over filterQuery and
  // listSnapshot. syncNow's deps are stable for the life of the page, so
  // capturing fetchData directly pinned the first render's copy - refreshing
  // after every sync as though no filter were typed and no snapshot loaded.
  const fetchDataRef = useRef(null)
  fetchDataRef.current = fetchData

  const observer = useRef()
  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1)
        }
      })
      if (node) observer.current.observe(node)
    },
    [isLoading, hasMore]
  )

  const refreshPending = useCallback(async () => {
    try {
      setPending(await pendingCount(id))
    } catch (error) {
      console.error('Could not read the scan queue:', error)
    }
  }, [id])

  // Sends queued scans in chunks. Each chunk is deleted from the queue only
  // once the server has ruled on it, so a connection that dies halfway leaves
  // the rest queued rather than losing them.
  const syncNow = useCallback(
    async (showToast = false) => {
      if (syncingRef.current) return
      syncingRef.current = true
      setIsSyncing(true)

      let applied = 0
      const rejected = []

      try {
        for (;;) {
          const chunk = await peekChunk(id, SYNC_CHUNK)
          if (chunk.length === 0) break

          const { data } = await axios.post(
            '/api/v1/admin/packing-list/sync-scans',
            {
              eventId: id,
              userId,
              ops: chunk.map(({ opId, code, action, scannedAt }) => ({
                opId,
                code,
                action,
                scannedAt,
              })),
            }
          )

          const results = data?.results || []
          // 'error' is the only transient verdict - those stay queued and are
          // retried. Everything else the server has decided on for good.
          const settled = results
            .filter((result) => result.status !== 'error')
            .map((result) => result.opId)

          applied += results.filter((r) => r.status === 'applied').length
          rejected.push(
            ...results.filter(
              (r) => r.status === 'not_found' || r.status === 'ambiguous'
            )
          )

          await removeOps(settled)
          // Nothing left the queue, so another pass would spin forever.
          if (settled.length === 0) break
        }

        if (applied > 0) {
          // The server owns the totals - re-read rather than trusting any
          // arithmetic done here.
          await fetchDataRef.current(1)
        }
        if (showToast && applied > 0) {
          toast.success(`Synced ${applied} scan${applied === 1 ? '' : 's'}`)
        }
        if (rejected.length > 0) {
          toast.warning(
            `${rejected.length} scan(s) could not be matched: ${rejected
              .map((r) => r.code)
              .join(', ')}`
          )
        }
      } catch (error) {
        console.error('Sync failed:', error)
        if (showToast) {
          toast.error('Sync failed - scans are still saved and will retry.')
        }
      } finally {
        syncingRef.current = false
        setIsSyncing(false)
        await refreshPending()
      }
    },
    [id, userId, refreshPending]
  )

  useEffect(() => {
    refreshPending()
  }, [refreshPending])

  // Ask once, on load, so the queue is exempt from automatic eviction long
  // before anyone starts scanning into it.
  useEffect(() => {
    requestPersistentStorage().then((granted) => {
      setStorageIsPersistent(granted)
      if (!granted) {
        console.warn(
          'Persistent storage was declined - queued scans could be evicted if the disk fills. Sync promptly.'
        )
      }
    })
  }, [])

  // Pulled once when offline mode is switched on - while there is still a
  // connection to pull it with. Only the loaded page of the table is in memory,
  // which is far too little to tell whether a Return is valid.
  useEffect(() => {
    if (!offlineMode || listSnapshot) return undefined
    let cancelled = false

    const loadCodes = async () => {
      try {
        const { data } = await axios.get(
          `/api/v1/admin/packing-list/details/${id}/codes`
        )
        if (cancelled) return
        const snapshot = new Map()
        ;(data?.codes || []).forEach((row) => {
          if (row.itemCode) snapshot.set(String(row.itemCode).toLowerCase(), row)
          if (row.barCode) snapshot.set(String(row.barCode).toLowerCase(), row)
        })
        setListSnapshot(snapshot)
      } catch (error) {
        console.error('Could not load packing list codes:', error)
        // Left null deliberately. Without the full list we must not reject a
        // Return that may well be valid, so validation stays off and the
        // server has the final say at sync.
        toast.warning(
          'Could not load the item list - offline scans will be checked at sync instead.'
        )
      }
    }

    loadCodes()
    return () => {
      cancelled = true
    }
  }, [offlineMode, listSnapshot, id])

  // Deliberately no automatic sync. Offline mode means offline: nothing goes
  // to the network until the operator presses Sync. Background retries would
  // put requests on a link the operator has just told us not to trust, and
  // would make it impossible to know whether a scan had been sent yet.

  // Closing the tab with scans still queued would strand them until someone
  // reopens this event on this same machine.
  useEffect(() => {
    if (pending === 0) return undefined
    const warn = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [pending])

  // Bumps the matching row and floats it to the top, so an offline scan looks
  // the same as an online one. The row stays flagged as pending until it has
  // actually reached the server. Returns false when the code is not on the
  // list, which offline we have no way to resolve - only a Take can do that,
  // and only the catalogue could tell us what the book is.
  const applyScanToList = (code, action) => {
    const key = String(code).trim().toLowerCase()
    const field = action === 'Take' ? 'takeQty' : 'returnQty'
    const fromSnapshot = listSnapshot?.get(key)

    // Read through the ref rather than state: the previous scan's row may not
    // have been rendered yet, and the old code decided its return value from
    // that stale state - claiming a visible item was not on screen.
    const prev = listRef.current
    const index = prev.findIndex((row) => codeMatchesRow(code, row))
    let next

    if (index !== -1) {
      const row = prev[index]
      next = [
        {
          ...row,
          [field]: (Number(row[field]) || 0) + 1,
          pendingScans: (row.pendingScans || 0) + 1,
        },
        ...prev.filter((_, i) => i !== index),
      ]
    } else if (fromSnapshot) {
      // The item is on the list but on a page that has not been lazy-loaded.
      // Pull it out of the snapshot and show it anyway - which page an item
      // happens to sit on should not decide whether a scan is visible.
      next = [
        {
          ...fromSnapshot,
          [field]: (Number(fromSnapshot[field]) || 0) + 1,
          pendingScans: 1,
        },
        ...prev,
      ]
    } else {
      // Not on this page and not in the snapshot - nothing to show yet.
      return false
    }

    listRef.current = next
    setPackingDataList(next)
    return true
  }

  const queueScan = async (code, action) => {
    const onList = listSnapshot
      ? listSnapshot.has(String(code).trim().toLowerCase())
      : null

    // A Return only ever applies to something already taken out for this event.
    // Reject it now, while the operator still has the book in hand, instead of
    // queueing work the server is certain to refuse at sync time.
    if (action === 'Return' && onList === false) {
      toast.error(`${code} is not on this packing list - nothing to return`)
      return
    }

    try {
      await enqueueScan({ eventId: id, code, action, userId })
      await refreshPending()

      const shownInList = applyScanToList(code, action)
      // Only worth saying for a Take that genuinely introduces a new item;
      // if it is already on the list it is simply on a page not loaded here.
      if (!shownInList && onList !== true) {
        toast.info(`${code} saved - it will appear in the list after sync`)
      }
    } catch (error) {
      console.error('Could not queue scan:', error)
      toast.error(`Could not save ${code} locally - re-scan it.`)
    }
  }

  const toggleEdit = (itemId, field, currentValue) => {
    if (editMode === itemId && editField === field) {
      setEditMode(null)
      setEditField(null)
      // Optionally reset to original value if not saved
      setPackingDataList((currentList) =>
        currentList.map((item) =>
          item._id === itemId
            ? {
                ...item,
                // ?? not || : an original value of 0 is valid and must not
                // fall through to the edited value.
                [field]: originalValues[`${itemId}-${field}`] ?? item[field],
              }
            : item
        )
      )
    } else {
      setEditMode(itemId)
      setEditField(field)
      // Set original value on first edit toggle
      setOriginalValues((prev) => ({
        ...prev,
        [`${itemId}-${field}`]: currentValue,
      }))
    }
  }

  const handleQtyChange = (itemId, value, type) => {
    setPackingDataList((currentList) =>
      currentList.map((item) =>
        item._id === itemId ? { ...item, [type]: value } : item
      )
    )
  }

  const saveQty = async (itemId, type) => {
    const item = packingDataList.find((item) => item._id === itemId)
    if (!item) return

    const originalValue = Number(
      String(originalValues[`${itemId}-${type}`]).trim()
    )
    const updatedValue = Number(String(item[type]).trim())

    if (originalValue === updatedValue) {
      setEditMode(null)
      setEditField(null)
      setFocusBarcodeInput(true)
      return
    }
    try {
      await axios.patch(`/api/v1/admin/packing-list/update-item/${itemId}`, {
        [type]: updatedValue,
        userId,
      })

      const userCode = auth?.user?.code

      // Prepare the updated product with the new quantity, user code, and date
      const updatedProduct = {
        ...item,
        [type]: updatedValue, // Store the number we actually sent, not the
        // raw string still sitting in the input
        lastUptByCode: userCode, // Update with the current user's code
        updatedAt: new Date(), // Set the current timestamp
      }

      setPackingDataList((prevItems) => [
        updatedProduct,
        ...prevItems.filter((item) => item._id !== itemId),
      ])

      setEditMode(null)
      setEditField(null)
      setFocusBarcodeInput(true)
    } catch (error) {
      console.error(`Failed to update ${type}:`, error)
      // Put the row back to its stored value. Without this the typed number
      // stays on screen looking saved, which is how a failed edit ends up
      // being discovered only at reconciliation time.
      setPackingDataList((currentList) =>
        currentList.map((row) =>
          row._id === itemId
            ? { ...row, [type]: originalValues[`${itemId}-${type}`] ?? row[type] }
            : row
        )
      )
      setEditMode(null)
      setEditField(null)
      toast.error(`Not saved: ${item.itemCode} ${type} reverted.`)
    }
  }

  const scanResult = async (code, action) => {
    try {
      const { data } = await axios.post(
        '/api/v1/admin/packing-list/scan-stock',
        { searchData: code, id, action, userId }
      )
      if (data.products.length === 0) {
        toast.info('No records found')
      } else {
        if (data.updatedProducts && data.updatedProducts.length > 0) {
          if (!data.isGroupItem) {
            updateScannedProduct(data.updatedProducts[0])
          } else {
            data.updatedProducts.forEach((updatedProduct) =>
              updateScannedProduct(updatedProduct)
            )
          }
        }
      }
    } catch (error) {
      // No response at all means a dead or timed-out link, and we cannot tell
      // whether the server applied it. Report this one for a re-scan and switch
      // to offline mode so the scans that follow are queued instead of lost.
      if (!error.response) {
        setOfflineMode(true)
        toast.error(
          `No connection - ${code} was NOT recorded. Re-scan it; offline mode is now on.`
        )
      } else {
        toast.error('Item not exist in packing list / AutoCount')
      }
    }
  }

  const updateScannedProduct = (scannedProduct) => {
    const userCode = auth?.user?.code

    setPackingDataList((prevItems) => {
      const existingItem = prevItems.find(
        (item) => item._id === scannedProduct._id
      )
      const updatedProduct = {
        ...scannedProduct,
        lastUptByCode: userCode,
        updatedAt: new Date(),
      }
      const isItemInList = existingItem !== undefined

      let updatedItems
      if (isItemInList) {
        updatedItems = [
          updatedProduct,
          ...prevItems.filter((item) => item._id !== scannedProduct._id),
        ]
      } else {
        if (totalPackingLists >= 30) {
          updatedItems = [
            updatedProduct,
            ...prevItems.slice(0, prevItems.length - 1),
          ]
        } else {
          updatedItems = [updatedProduct, ...prevItems]
        }
      }

      return updatedItems
    })
  }

  const exportFile = async () => {
    try {
      const response = await axios.get(
        `/api/v1/admin/packing-list/export/${id}`,
        { responseType: 'blob' }
      )

      // Extract filename from content-disposition header
      const contentDisposition = response.headers['content-disposition']
      let filename = 'download.xlsx' // Default filename if not specified
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
        // match() returns null when the header carries no filename - reading
        // .length off that threw, and the file was never handed to saveAs.
        if (filenameMatch && filenameMatch.length === 2) {
          filename = filenameMatch[1]
        }
      }

      saveAs(response.data, filename)
    } catch (error) {
      console.error('Failed to download the file:', error)
      toast.error('Failed to download the file.')
    }
  }

  const exportSaleFile = async () => {
    try {
      const response = await axios.get(
        `/api/v1/admin/packing-list/export-sale/${id}`,
        { responseType: 'blob' }
      )

      // Check if backend returned JSON (no data case)
      const contentType = response.headers['content-type']

      if (contentType && contentType.includes('application/json')) {
        const reader = new FileReader()
        reader.onload = () => {
          try {
            const json = JSON.parse(reader.result)
            if (json && json.message) {
              toast.info(json.message)
            } else {
              toast.info('No mismatch found - take, return and sale all agree.')
            }
          } catch (err) {
            toast.info('No mismatch found - take, return and sale all agree.')
          }
        }
        reader.readAsText(response.data)
        return
      }

      // Otherwise proceed with normal Excel download
      const contentDisposition = response.headers['content-disposition']
      let filename = 'download.xlsx'
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
        if (filenameMatch && filenameMatch.length === 2) {
          filename = filenameMatch[1]
        }
      }

      saveAs(response.data, filename)
    } catch (error) {
      console.error('Failed to download the file:', error)
      toast.error('Failed to download the file.')
    }
  }

  // The file inputs are hidden behind buttons, so choosing a file is the whole
  // interaction - there is no separate "now upload it" click, and the two large
  // file controls no longer sit in the toolbar.
  const handleImportFile = (event) => {
    const file = event.target.files[0]
    if (file) uploadFile(file)
  }

  const uploadFile = async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    setIsLoadingImport(true)

    try {
      // axios, not fetch: the auth token lives in axios' default headers, so a
      // raw fetch reaches this route - which sits behind requireSignIn - with
      // no Authorization header at all and is rejected as 'No token provided!'.
      // timeout 0 overrides the global 15s default, which is sized for scans;
      // a large sales file can legitimately take longer than that to upload.
      await axios.post(`/api/v1/admin/packing-list/import/${id}`, formData, {
        timeout: 0,
      })
      fetchData()
      toast.success('Sales report imported')
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Sales report import failed')
    } finally {
      setIsLoadingImport(false)
      // Reset the input so picking the same file again still fires onChange.
      if (fileImport.current) fileImport.current.value = ''
    }
  }

  const handleImportMasterFile = (event) => {
    const file = event.target.files[0]
    if (file) uploadMasterFile(file)
  }

  const uploadMasterFile = async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('userId', userId)
    setIsLoadingMasterImport(true)

    try {
      // Same as the sales import: must go through axios to carry the token.
      await axios.post(
        `/api/v1/admin/packing-list/import/master/${id}`,
        formData,
        { timeout: 0 }
      )
      fetchData()
      toast.success('Master file imported')
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Master file import failed')
    } finally {
      setIsLoadingMasterImport(false)
      if (masterFileImport.current) masterFileImport.current.value = ''
    }
  }

  const handleKeyPress = async (event) => {
    if (event.key !== 'Enter') return
    event.preventDefault()

    const code = searchData.trim()
    if (!code) return

    // Capture the action now: in offline mode the send happens later, and the
    // operator may have switched between Take and Return by then.
    const action = optionEnter

    // Clear immediately rather than after the response. A barcode scanner types
    // straight into this box, so leaving the previous code in it while a slow
    // request finishes is how two barcodes end up concatenated into one.
    setSearchData('')
    setFocusBarcodeInput(true)

    if (offlineMode) {
      await queueScan(code, action)
      return
    }
    await scanResult(code, action)
  }

  const handleQtyKeyPress = (event, itemId, type) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveQty(itemId, type)
    }
  }

  const deleteData = async (itemId) => {
    try {
      if (window.confirm('Confirm Delete?')) {
        await axios.delete(`/api/v1/admin/packing-list/details/${itemId}`)
        fetchData()
      }
    } catch (error) {
      console.error('Failed to delete item:', error)
    }
  }

  const clearInput = () => {
    setSearchData('')
    setFocusBarcodeInput(true)
  }

  const getSearchContent = (value) => {
    setSearchData(value)
  }

  const handleFilterChange = (value) => {
    setFilterQuery(value)
  }

  const closeModal = (e) => {
    e.preventDefault()
    setVisibleLogModal(false)
  }

  const openLogModal = (itemId) => {
    const item = packingDataList.find((item) => item._id === itemId)
    if (item) {
      setCurrentItemId(itemId)
      setCurrentItemDetails({
        itemCode: item.itemCode,
        description: item.description,
      })
      setVisibleLogModal(true)
    }
  }

  return (
    <Layout title={'Packing List'}>
      {/* Sticky rather than fixed with a hard-coded 120px height, matching the
          stock check pages: the toolbar can wrap on a narrow screen without
          covering the first rows of the table. z-index stays below antd's
          modal base of 1000. */}
      <div
        className='shadow-sm border-bottom px-3 px-md-4 pt-3 pb-3 bg-white'
        style={{ position: 'sticky', top: '50px', zIndex: 900 }}
      >
        {/* Where you are, and finding a row */}
        <div className='wms-toolbar d-flex align-items-center flex-wrap gap-3 mb-3'>
          <nav aria-label='breadcrumb' className='me-auto'>
            <ol className='breadcrumb wms-breadcrumb mb-0'>
              <li className='breadcrumb-item'>
                <button
                  type='button'
                  className='btn btn-link p-0 text-decoration-none'
                  onClick={() => navigate('/admin/packing-list')}
                >
                  Packing List
                </button>
              </li>
              <li className='breadcrumb-item active' aria-current='page'>
                <span className='wms-crumb-current'>Details</span>
              </li>
            </ol>
          </nav>

          <div className='d-flex'>
            <input
              type='text'
              className='form-control'
              style={{ width: '250px', maxWidth: '100%' }}
              placeholder='Search Item Code or Desc'
              onChange={(e) => handleFilterChange(e.target.value)}
              value={filterQuery}
            />
            <button
              className='btn btn-secondary'
              onClick={() => setFilterQuery('')}
            >
              Clear
            </button>
          </div>

          {/* Each button keeps the hue it has always had, so nothing has to
              be relearned - but outlined rather than filled. Fill is now the
              one signal that matters: outline reads, solid writes, and
              Import Sales is the only filled button on the bar. */}
          <button
            type='button'
            className='btn btn-outline-info'
            onClick={() => setVisibleQtyModal(true)}
          >
            Qty Details
          </button>
          <button
            type='button'
            className='btn btn-outline-success'
            onClick={() => exportFile()}
          >
            Export List
          </button>
          {/* Grouped by subject: left of this rule is the packing list,
              right of it is the sales file - the import that brings it in and
              the mismatch report that comes out of it. Import stays the only
              solid button on the bar because it is the only one that writes. */}
          <div className='vr align-self-center' style={{ height: '2rem' }} />

          {/* The file inputs are hidden - the button opens the picker and the
              upload starts as soon as a file is chosen. */}
          {SHOW_IMPORT_MASTER && (
            <>
              <input
                type='file'
                className='d-none'
                onChange={handleImportMasterFile}
                ref={masterFileImport}
              />
              <button
                type='button'
                className='btn btn-primary'
                onClick={() => masterFileImport.current?.click()}
                disabled={isLoadingMasterImport}
              >
                {isLoadingMasterImport ? 'Importing...' : 'Import Master'}
              </button>
            </>
          )}

          <input
            type='file'
            className='d-none'
            onChange={handleImportFile}
            ref={fileImport}
          />
          <button
            type='button'
            className='btn btn-primary'
            onClick={() => fileImport.current?.click()}
            disabled={isLoadingImport}
          >
            {isLoadingImport ? 'Importing...' : 'Import Sales'}
          </button>

          {/* Sits after Import Sales because that is the order the work
              happens in: bring the sales file in, then check what disagrees
              with it. Hidden until a sales file has been imported - for most
              of a shift it would be dead weight on a crowded bar, and pressed
              before the figures land it would only list everything still out.
              fetchData runs after the import, so it appears by itself. */}
          {hasSaleData && (
            <button
              type='button'
              className='btn btn-outline-warning'
              onClick={() => exportSaleFile()}
              title='Export the items where take - return does not equal sale'
            >
              Mismatch
            </button>
          )}

          <div className='vr align-self-center' style={{ height: '2rem' }} />

          <button
            type='button'
            className='btn btn-outline-info'
            onClick={() => fetchData(1, true)}
            aria-label='Refresh'
            title='Refresh'
          >
            <i className='bi bi-arrow-clockwise'></i>
          </button>
        </div>

        {/* Scanning - the one row that matters while work is happening */}
        <div className='d-flex align-items-center flex-wrap gap-3'>
          {/* A segmented control rather than loose radios: which mode you are
              in decides whether a scan adds or subtracts, so it has to be
              obvious at a glance. The selected side renders solid. */}
          <div className='btn-group' role='group' aria-label='Scan action'>
            <input
              type='radio'
              className='btn-check'
              name='action'
              id='takeRadio'
              checked={optionEnter === 'Take'}
              onChange={() => setOptionEnter('Take')}
            />
            <label className='btn btn-outline-primary' htmlFor='takeRadio'>
              Take
            </label>
            <input
              type='radio'
              className='btn-check'
              name='action'
              id='returnRadio'
              checked={optionEnter === 'Return'}
              onChange={() => setOptionEnter('Return')}
            />
            <label className='btn btn-outline-primary' htmlFor='returnRadio'>
              Return
            </label>
          </div>

          <div className='d-flex flex-grow-1' style={{ minWidth: '240px' }}>
            <input
              type='text'
              className='form-control'
              value={searchData}
              placeholder='Scan / Enter Item Code or Barcode'
              onChange={(e) => getSearchContent(e.target.value)}
              onKeyDown={handleKeyPress}
              style={{ flex: 1 }}
              ref={barcodeInputRef}
              autoFocus
            />
            <button
              type='button'
              className='btn btn-secondary'
              disabled={!searchData}
              onClick={clearInput}
            >
              Clear
            </button>
          </div>

          <div className='form-check form-switch mb-0'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              id='offlineModeToggle'
              checked={offlineMode}
              onChange={(e) => setOfflineMode(e.target.checked)}
            />
            <label className='form-check-label' htmlFor='offlineModeToggle'>
              Offline
              {offlineMode && !storageIsPersistent && (
                <i
                  className='bi bi-exclamation-triangle-fill ms-1'
                  style={{ color: '#ffc107' }}
                  title='This browser would not grant persistent storage, so queued scans could be evicted if the disk fills. Sync often.'
                ></i>
              )}
            </label>
          </div>

          {(offlineMode || pending > 0) && (
            <button
              type='button'
              className={pending > 0 ? 'btn btn-warning' : 'btn btn-secondary'}
              onClick={() => syncNow(true)}
              disabled={isSyncing || pending === 0}
              title={
                pending > 0
                  ? `${pending} scan(s) saved on this device, not yet in the database`
                  : 'Everything is synced'
              }
            >
              {isSyncing
                ? 'Syncing...'
                : pending > 0
                ? `Sync ${pending}`
                : 'Synced'}
            </button>
          )}
        </div>

      </div>

      <style>{`
        .wms-breadcrumb {
          --bs-breadcrumb-divider: '\\203A';
          font-size: 0.95rem;
          align-items: center;
        }
        .wms-breadcrumb .breadcrumb-item {
          display: flex;
          align-items: center;
        }
        .wms-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
          color: #adb5bd;
          padding: 0 0.6rem;
          font-size: 1.1rem;
        }
        .wms-crumb-current {
          font-weight: 600;
        }
        /* Bootstrap draws outline-info and outline-warning labels in the raw
           #0dcaf0 and #ffc107, which sit at roughly 1.6:1 on this white bar -
           unreadable under warehouse lighting. Keep the hue on the border and
           darken only the text, using Bootstrap's own *-text-emphasis values.
           The filled hover state already has enough contrast, so it is left
           alone. */
        .wms-toolbar .btn-outline-info {
          color: #087990;
        }
        .wms-toolbar .btn-outline-warning {
          color: #997404;
        }
        .wms-toolbar .btn-outline-info:hover,
        .wms-toolbar .btn-outline-info:focus,
        .wms-toolbar .btn-outline-warning:hover,
        .wms-toolbar .btn-outline-warning:focus {
          color: #000;
        }
      `}</style>

      <div>
        <div className='table-responsive'>
          <AdminPackingListTable
            packingDataList={packingDataList}
            lastElementRef={lastElementRef}
            editMode={editMode}
            editField={editField}
            handleQtyChange={handleQtyChange}
            saveQty={saveQty}
            handleQtyKeyPress={handleQtyKeyPress}
            toggleEdit={toggleEdit}
            openLogModal={openLogModal}
            deleteData={deleteData}
          />
        </div>
      </div>

      <Modal
        closable={false}
        onCancel={() => setVisibleLogModal(false)}
        open={visibleLogModal}
        width={'70%'}
        footer={[
          <Button key='cancel' onClick={() => setVisibleLogModal(false)}>
            Cancel
          </Button>,
        ]}
      >
        <QtyLog
          closeLogModal={closeModal}
          itemId={currentItemId}
          itemDetails={currentItemDetails}
          lastUpdate={lastUpdate}
        />
      </Modal>

      <Modal
        closable={false}
        onCancel={() => setVisibleQtyModal(false)}
        open={visibleQtyModal}
        footer={[
          <Button key='cancel' onClick={() => setVisibleQtyModal(false)}>
            Cancel
          </Button>,
        ]}
      >
        <ItemQty
          totalItems={quantities.totalItems}
          totalTakeQty={quantities.totalTakeQty}
          totalReturnQty={quantities.totalReturnQty}
          totalSaleQty={quantities.totalSaleQty}
        />
      </Modal>
    </Layout>
  )
}

export default AdminPackingListDetails
